import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Link,
  Tooltip,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Home as HomeIcon,
  OpenInNew as ExternalIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Hub as HubIcon,
  GppMaybe as ExceptionIcon,
  FactCheck as AttestationIcon,
  Explore as ExploreIcon,
  Visibility as VisibilityIcon,
  AccountTree as AccountTreeIcon,
  WorkspacePremium as CertViewIcon,

  Build as HelpdeskIcon,
  Assessment as ReportsIcon,
  ManageAccounts as ManagementToolsIcon,
  Handyman as UserToolsIcon,
  AdminPanelSettings as AdminToolsIcon,
  AddTask as WorkRequestIcon,
  Inbox as TeamBacklogIcon,
  Groups as ManageTeamIcon,
  ListAlt as MyRequestsIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { layout, palette } from '../ui-theme';
import { useAuth } from '../AuthContext';
import { useEnvironment, type Features } from '../EnvironmentContext';
import { isFeatureEnabled, type FeatureKey } from '../featureFlags';
import { useTeamAccess, isVisible, type TeamAccess } from '../TeamAccessContext';
import { canSeeAnyTile, type TileContext } from './ToolTileGrid';
import { allAdminTools, managementTools, reportTiles, userTools } from './toolCatalog';
import { isAccessibilityAuditor } from '../accessibilityAudit';


interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  matchPrefix?: boolean;
  /**
   * Overrides the active-path test. Needed for entries that open a landing page whose destinations
   * live at unrelated paths: without it, standing on a report highlights nothing at all.
   */
  activeWhen?: (pathname: string) => boolean;
  requireRole?: (roles: string[]) => boolean;
  /** Optional feature-flag gate, named by catalog key; the item is hidden unless the flag is on. */
  requireFeature?: FeatureKey;
  /**
   * Gate needing both roles and flags at once — used by entries that open a tile page, so the
   * entry is shown exactly when at least one tile on that page is visible.
   */
  requireAccess?: (ctx: TileContext) => boolean;
  /** Optional team-membership gate for surfaces you reach by belonging to a team, not by role. */
  requireTeamAccess?: (access: TeamAccess) => boolean;
  /**
   * True when the item is shown on an access answer the server could not confirm. The entry still
   * renders — hiding a surface the user needs explains nothing — but it says so on hover, so an
   * unverified affordance is never indistinguishable from a confirmed one.
   */
  unverified?: (access: TeamAccess) => boolean;
}

interface NavSection {
  /** Section header caption; omit for the top group, which has no header. */
  label?: string;
  ariaLabel: string;
  items: NavItem[];
}

// Admin Tools paths not already claimed by their own first-class sidebar entry elsewhere (e.g.
// /work-items/teams is "Manage Team" under Intake Management) - claiming those here too would
// highlight two sidebar items at once for the same page. See the Admin Tools activeWhen below.
const adminOnlyToolPaths = allAdminTools
  .map((t) => t.path)
  .filter((p) => p !== '/work-items/teams');

const navSections: NavSection[] = [
  {
    ariaLabel: 'Main navigation',
    items: [
      { label: 'Home', path: '/', icon: <HomeIcon /> },
      { label: 'Request Central', path: '/requests', icon: <HubIcon /> },
      { label: 'Exception Central', path: '/exceptions', icon: <ExceptionIcon /> },
      {
        label: 'Attestation Central',
        path: '/attestations',
        icon: <AttestationIcon />,
        requireFeature: 'IdentityTools.AttestationCentral',
      },
      { label: 'Request Engineering Work', path: '/work-requests/new', icon: <WorkRequestIcon /> },
      { label: 'My Requests', path: '/my-requests', icon: <MyRequestsIcon /> },
      // Guest Pass and IAM Agent Assistant intentionally omitted here — both are surfaced as
      // tiles in User Tools instead of duplicated as direct nav entries.
      {
        label: 'User Tools',
        path: '/user-tools',
        icon: <UserToolsIcon />,
        // Derived from the tile catalog rather than restated here, so gating a user tool cannot
        // leave this entry pointing at a page with nothing on it.
        requireAccess: (ctx) => canSeeAnyTile(userTools, ctx),
        // Every User Tools tile (Guest Pass, Onboarding Buddy, IAM Agent Assistant, etc.) is
        // reachable only through this tile page, not its own first-class sidebar entry, so
        // nothing else can claim these paths - matching them here is always safe.
        activeWhen: (p) => p === '/user-tools' || userTools.some((t) => t.path === p),
      },
      {
        label: 'Helpdesk Tools',
        path: '/helpdesk',
        icon: <HelpdeskIcon />,
        matchPrefix: true,
        requireRole: (roles) => roles.some((r) => r === 'AskIAM.Helpdesk.Read' || r === 'AskIAM.Admin'),
        requireFeature: 'IdentityTools.Helpdesk',
      },
    ],
  },
  {
    label: 'Intake Management',
    ariaLabel: 'Intake management',
    items: [
      { label: 'Team Backlog', path: '/work-requests/triage', icon: <TeamBacklogIcon />, requireTeamAccess: (a) => isVisible(a.canTriage), unverified: (a) => a.canTriage === 'unknown' },
      { label: 'Manage Team', path: '/work-items/teams', icon: <ManageTeamIcon />, requireTeamAccess: (a) => isVisible(a.canManage), unverified: (a) => a.canManage === 'unknown' },
    ],
  },
  {
    label: 'Administration',
    ariaLabel: 'Administration',
    items: [
      // Conditional Access Readiness and CA Incident Investigation intentionally omitted here —
      // both are already surfaced as tiles within Admin Tools, so a direct nav entry would just
      // duplicate them.
      {
        label: 'Admin Tools',
        path: '/admin-tools',
        icon: <AdminToolsIcon />,
        // Derived from the tile catalog rather than restated here, so a tool added or re-gated
        // later cannot leave the nav entry pointing at a page with nothing on it.
        requireAccess: (ctx) => canSeeAnyTile(allAdminTools, ctx),
        // Only the subset of Admin Tools paths without their own first-class sidebar entry
        // elsewhere (adminOnlyToolPaths excludes /work-items/teams, which is "Manage Team" under
        // Intake Management) - claiming that one here too would highlight two items at once.
        activeWhen: (p) => p === '/admin-tools' || adminOnlyToolPaths.includes(p),
      },
      {
        label: 'Management Tools',
        path: '/management-tools',
        icon: <ManagementToolsIcon />,
        requireAccess: (ctx) => canSeeAnyTile(managementTools, ctx),
        activeWhen: (p) => p === '/management-tools' || managementTools.some((t) => t.path === p),
      },
      {
        label: 'Reports',
        path: '/reports',
        icon: <ReportsIcon />,
        requireAccess: (ctx) => canSeeAnyTile(reportTiles, ctx),
        activeWhen: (p) => p === '/reports' || p.startsWith('/reports/category/') || reportTiles.some((t) => t.path === p),
      },
    ],
  },
];

interface ExternalLink {
  label: string;
  href: string;
  icon: React.ReactNode;
  requireRole?: (roles: string[]) => boolean;
}

const externalLinks: ExternalLink[] = [
  {
    label: 'S360 EOS',
    href: 'https://aka.ms/iamps360',
    icon: <ExploreIcon />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Ext.S360.Read' || r === 'AskIAM.Admin'),
  },
  {
    label: 'RAVE',
    href: 'https://aka.ms/pimviz',
    icon: <VisibilityIcon />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Ext.RAVE.Read' || r === 'AskIAM.Admin'),
  },
  {
    label: 'UAMI Lifecycle',
    href: 'https://aka.ms/uamilifecycle',
    icon: <AccountTreeIcon />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Ext.UAMI.Read' || r === 'AskIAM.Admin'),
  },
  {
    label: 'CertView',
    href: 'https://aka.ms/certview',
    icon: <CertViewIcon />,
  },

];

interface SidebarProps {
  collapsed: boolean;
  mobile: boolean;
  open: boolean;
  onClose: () => void;
}



const Sidebar: React.FC<SidebarProps> = ({ collapsed, mobile, open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { features, flags, configurationProfile } = useEnvironment();
  const restricted = configurationProfile === 'IAMPProdOnly';
  const teamAccess = useTeamAccess();
  // Collapsed by default every session (not persisted) - these are unrelated external tools, not
  // core AskIAM navigation, so they shouldn't compete with it for attention on first paint.
  const [externalLinksOpen, setExternalLinksOpen] = React.useState(false);

  const width = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;

  const userRoles = user?.roles ?? [];
  const auditReadOnly = isAccessibilityAuditor(userRoles);

  const resolvedExternalLinks = React.useMemo(() => {
    return (restricted ? [] : externalLinks)
      .filter((link) => !link.requireRole || link.requireRole(userRoles));
  }, [userRoles, restricted]);

  const visibleSections = React.useMemo(
    () =>
      navSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) =>
              (!restricted || ['/requests', '/my-requests', '/admin-tools'].includes(item.path)) &&
              (auditReadOnly || !item.requireRole || item.requireRole(userRoles)) &&
              (!item.requireFeature || isFeatureEnabled(flags, item.requireFeature)) &&
              (!item.requireAccess || item.requireAccess({ roles: userRoles, features, flags, teamAccess })) &&
              (auditReadOnly || !item.requireTeamAccess || item.requireTeamAccess(teamAccess)),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [userRoles, features, teamAccess, flags, restricted],
  );

  const renderNavItem = (item: NavItem) => {
    const isActive = item.activeWhen
      ? item.activeWhen(location.pathname)
      : item.matchPrefix
        ? location.pathname.startsWith(item.path)
        : location.pathname === item.path;
    const unverified = item.unverified?.(teamAccess) ?? false;
    const button = (
      <ListItemButton
        key={item.path}
        onClick={() => navigate(item.path)}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          mx: 0,
          py: 1,
          px: collapsed ? 0 : 2.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderLeft: `3px solid ${isActive ? palette.sidebar.activeBorder : 'transparent'}`,
          bgcolor: isActive ? palette.sidebar.activeBg : 'transparent',
          color: isActive ? palette.sidebar.textActive : palette.sidebar.text,
          '&:hover': {
            bgcolor: palette.sidebar.hoverBg,
            color: palette.sidebar.textHover,
          },
        }}
      >
        <ListItemIcon sx={{ color: 'inherit', minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 700 : 400 }}
          />
        )}
      </ListItemButton>
    );

    // <List> renders a <ul>, so its children must be <li>. ListItemButton renders a
    // <div>/<a>, which makes the list invalid to a screen reader (axe: `list`).
    // ListItem supplies the <li> wrapper; disablePadding keeps the existing spacing.
    return (
      <ListItem disablePadding key={item.path}>
        {collapsed || unverified ? (
          <Tooltip
            title={unverified ? `${item.label} — access could not be verified right now, so this may not open.` : item.label}
            placement="right"
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      sx={{
        width: mobile ? 0 : width,
        flexShrink: 0,
        transition: 'width 0.2s',
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          backgroundColor: palette.sidebar.background,
          // Same reason as the AppBar: the theme rounds every Paper, and a
          // full-height drawer is not something that should have corners.
          borderRadius: 0,
          color: '#fff',
          borderRight: 'none',
          position: mobile ? 'fixed' : 'relative',
          transition: 'width 0.2s',
          overflowX: 'hidden',
        },
      }}
    >
      {/*
        The whole sidebar is one navigation landmark. Without it the brand block
        and the footer sit outside every landmark, so a screen reader user who
        navigates by landmark never reaches them (axe: region). The wrapper has to
        repeat the drawer paper's flex column, because the footer positions itself
        with `mt: 'auto'` and would otherwise no longer be pushed to the bottom.
      */}
      <Box
        component="nav"
        id="sidebar-navigation"
        aria-label="Sidebar"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
      {!collapsed && (
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${palette.sidebar.borderColor}` }}>
          <Typography variant="h1" component="div" sx={{ color: palette.sidebar.textActive }}>
            AskIAM
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            Identity and Access Support Portal
          </Typography>
          {!restricted && <Link
            href="https://eng.ms/docs/microsoft-security/ciso-organization/iamprotect"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              px: 1,
              py: 0.5,
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 1,
              color: 'rgba(255,255,255,0.8)',
              textDecoration: 'none',
              fontSize: '0.8rem',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
              },
            }}
          >
            <ExternalIcon sx={{ fontSize: 14 }} />
            IAM Protect
          </Link>}
        </Box>
      )}

      {visibleSections.map((section, idx) => (
        <Box key={section.ariaLabel}>
          {idx > 0 && (
            <Divider sx={{ borderColor: palette.sidebar.borderColor, mx: collapsed ? 0 : 2 }} />
          )}
          {section.label && !collapsed && (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 2.5,
                pt: 1.5,
                pb: 0.25,
                // Stated as a solid token rather than an alpha: 0.45 composited
                // to #7f8595 (4.30), 0.55 to #969ba8 (5.71), and neither number
                // is visible from the source you would edit.
                color: palette.sidebar.textMuted,
                fontSize: '0.68rem',
                letterSpacing: '0.08em',
                lineHeight: 1.8,
              }}
            >
              {section.label}
            </Typography>
          )}
          <List aria-label={section.ariaLabel} sx={{ py: section.label && !collapsed ? 0.5 : 1 }}>
            {section.items.map(renderNavItem)}
          </List>
        </Box>
      ))}

      {!restricted && resolvedExternalLinks.length > 0 && <>
      <Divider sx={{ borderColor: palette.sidebar.borderColor, mx: collapsed ? 0 : 2 }} />
      <Box>
        {collapsed ? (
          // Icon rail has no room for a label, and there's no clutter to hide at this
          // density - keep these always visible here, same as every other icon-only entry.
          <List aria-label="External links" sx={{ py: 1 }}>
            {resolvedExternalLinks.map((link) => {
              const button = (
                <ListItemButton
                  key={link.href}
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${link.label} (opens in new tab)`}
                  sx={{
                    mx: 0,
                    py: 1,
                    px: 0,
                    justifyContent: 'center',
                    borderLeft: '3px solid transparent',
                    color: palette.sidebar.text,
                    '&:hover': {
                      bgcolor: palette.sidebar.hoverBg,
                      color: palette.sidebar.textHover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 0, justifyContent: 'center' }}>
                    {link.icon}
                  </ListItemIcon>
                </ListItemButton>
              );
              return (
                <ListItem disablePadding key={link.href}>
                  <Tooltip title={link.label} placement="right">
                    {button}
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <>
            {/* These are external tools, not core AskIAM navigation, so they stay collapsed
                behind this toggle instead of competing with real nav items by default. */}
            <ListItemButton
              onClick={() => setExternalLinksOpen((v) => !v)}
              aria-expanded={externalLinksOpen}
              aria-controls="sidebar-external-links"
              sx={{ px: 2.5, py: 1, color: palette.sidebar.textMuted }}
            >
              <ListItemText
                primary="Other Tools"
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
              />
              {externalLinksOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </ListItemButton>
            <Collapse in={externalLinksOpen} timeout="auto" unmountOnExit>
              <List id="sidebar-external-links" aria-label="External links" sx={{ py: 1 }}>
                {resolvedExternalLinks.map((link) => {
                  const button = (
                    <ListItemButton
                      key={link.href}
                      component="a"
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${link.label} (opens in new tab)`}
                      sx={{
                        mx: 0,
                        py: 1,
                        px: 2.5,
                        justifyContent: 'flex-start',
                        borderLeft: '3px solid transparent',
                        color: palette.sidebar.text,
                        '&:hover': {
                          bgcolor: palette.sidebar.hoverBg,
                          color: palette.sidebar.textHover,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'inherit', minWidth: 36, justifyContent: 'center' }}>
                        {link.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                      <ExternalIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5 }} />
                    </ListItemButton>
                  );
                  return <ListItem disablePadding key={link.href}>{button}</ListItem>;
                })}
              </List>
            </Collapse>
          </>
        )}
      </Box>
      </>}

      {/* Footer */}
      {!restricted && <Box
        sx={{
          mt: 'auto',
          py: 1.5,
          px: collapsed ? 1 : 2.5,
          borderTop: `1px solid ${palette.sidebar.borderColor}`,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: palette.sidebar.hoverBg },
        }}
        onClick={() => navigate('/contributors')}
      >
        <Typography
          variant="caption"
          sx={{ color: palette.sidebar.textMuted, fontSize: collapsed ? 10 : 12 }}
        >
          {collapsed ? '❤️' : 'Built with ❤️ by our Contributors'}
        </Typography>
      </Box>}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
